import cx from "classnames"
import { useState, useEffect } from "react"

import type { BottomNavbarProps, BottomNavItem } from "./types"

import { Badge } from "../../atoms/badge"
import { Sidebar } from "../../atoms/sidebar"
import css from "./index.module.css"

interface MenuProps {
  readonly isOpen: boolean
  readonly menuItems: BottomNavbarProps["menuItems"]
  readonly currentRoute: string
  readonly onRouteChange?: (url: string) => void
  readonly onClose: () => void
}

const Menu = ({ isOpen, menuItems, currentRoute, onRouteChange, onClose }: MenuProps) => {
  return (
    <div className={cx(css.menuBackground, { [css.open]: isOpen })} onClick={onClose}>
      <div className={cx(css.menuContent, { [css.open]: isOpen })} onClick={(e) => e.stopPropagation()}>
        <div className={css.menu}>
          <Sidebar menu={menuItems} currentRoute={currentRoute} onRouteChange={onRouteChange} />
        </div>
      </div>
    </div>
  )
}

interface BottomItemProps extends BottomNavItem {
  readonly currentRoute: string
  readonly showLabel?: boolean
  readonly onRouteChange?: (url: string) => void
}

const BottomItem = ({ url, icon: Icon, label, onClick, tag, currentRoute, showLabel, onRouteChange }: BottomItemProps) => {
  const handleClick = () => {
    if (onClick) onClick()
    if (url) onRouteChange?.(url)
  }
  return (
    <div
      className={cx(css.navItem, {
        [css.current]: currentRoute === url,
        [css.withLabel]: showLabel,
      })}
      onClick={handleClick}
    >
      <div className={css.navIcon}>
        <Icon />
        {tag && <span className={css.tag}><Badge>{tag}</Badge></span>}
      </div>
      {showLabel && <span className={css.label}>{label}</span>}
    </div>
  )
}

const BottomNavbarComponent = ({
  bottomItems,
  menuItems,
  currentRoute,
  showLabels,
  onOpenChange,
  onRouteChange,
}: BottomNavbarProps) => {
  const [open, setOpen] = useState(false)
  const hasMenuItems = !!menuItems?.length

  useEffect(() => {
    setOpen(false)
  }, [currentRoute])

  const toggleOpen = () => {
    const next = !open
    setOpen(next)
    if (onOpenChange) {
      onOpenChange(next)
    }
  }

  // Cerrar el menú
  const closeMenu = () => {
    setOpen(false)
    if (onOpenChange) {
      onOpenChange(false)
    }
  }

  const maxVisibleItems = hasMenuItems ? 4 : 5

  return (
    <>
      <div className={css.navbar}>
        {bottomItems.slice(0, maxVisibleItems).map((item) => (
          <BottomItem key={item.label} {...item} currentRoute={currentRoute} showLabel={showLabels} onRouteChange={onRouteChange} />
        ))}
        {hasMenuItems && (
          <div className={css.menuButtonItem}>
            <label className={css.burger} htmlFor="burger">
              <input onClick={toggleOpen} type="checkbox" id="burger" checked={open} readOnly />
              <span></span>
              <span></span>
              <span></span>
            </label>
          </div>
        )}
      </div>

      {hasMenuItems && (
        <Menu
          isOpen={open}
          menuItems={menuItems}
          currentRoute={currentRoute}
          onRouteChange={onRouteChange}
          onClose={closeMenu}
        />
      )}
    </>
  )
}

export const BottomNavbar = BottomNavbarComponent

export type { BottomNavbarProps, BottomNavItem } from "./types"
